import { useMemo } from 'react';
import { createAvatar } from '@dicebear/core';
import * as adventurerNeutral from '@dicebear/adventurer-neutral';
import { AVATAR_SEEDS } from '../config/avatarConfig';

const PASTEL_COLORS = [
  'bg-pink-200 text-pink-700',
  'bg-purple-200 text-purple-700',
  'bg-blue-200 text-blue-700',
  'bg-emerald-200 text-emerald-700',
  'bg-amber-200 text-amber-700',
  'bg-rose-200 text-rose-700',
];

const SIZE_CLASSES = {
  xs:  'w-7 h-7 text-xs',
  sm:  'w-8 h-8 text-sm',
  md:  'w-10 h-10 text-base',
  lg:  'w-20 h-20 text-3xl',
  xl:  'w-24 h-24 text-4xl',
};

/**
 * Renders a user avatar.
 * - avatar === 0 or null/undefined → first letter of username on a pastel background
 * - avatar 1–20 → DiceBear adventurer-neutral generated client-side from a seed
 *
 * @param {number}  avatar    - integer 0–20 from Firebase
 * @param {string}  username  - used for letter fallback and pastel color derivation
 * @param {'sm'|'md'|'lg'|'xl'} size
 */
export default function AvatarDisplay({ avatar, username, size = 'md' }) {
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  const svgDataUri = useMemo(() => {
    const index = typeof avatar === 'number' ? avatar : 0;
    const seed = AVATAR_SEEDS[index];
    if (!seed) return null;

    const svg = createAvatar(adventurerNeutral, {
      seed,
      size: 128,
      backgroundColor: ['fde68a', 'fbcfe8', 'ddd6fe', 'bbf7d0', 'bae6fd'],
    }).toString();

    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }, [avatar]);

  if (!svgDataUri) {
    const letter = (username || '?').charAt(0).toUpperCase();
    const colorClass = PASTEL_COLORS[(username || '').charCodeAt(0) % PASTEL_COLORS.length];
    return (
      <div className={`${sizeClass} ${colorClass} rounded-full flex items-center justify-center font-bold shrink-0 select-none`}>
        {letter}
      </div>
    );
  }

  return (
    <img
      src={svgDataUri}
      alt="avatar"
      className={`${sizeClass} rounded-full object-cover shrink-0`}
    />
  );
}
